import { FlowButtons, NewOrdersContent, NewOrdersContentContainer } from "components/NewOrders/NewOrders.styles";
import {
	ContactCare,
	NewOrderBack,
	NewOrderGenericError,
	NewOrderNotCorrect,
	NewOrderProcessError,
	NewOrderReviewPageTitle,
	NewOrderSubmitOrder,
	ProQuoteReviewPageTitle,
	SaveAndDownloadNewOrderQuote,
	SaveDraftHangTightText,
	SaveDraftSuccessfullySavedText,
	SubmitQuoteText
} from "components/NewOrders/constants";
import NewOrdersSubheader from "components/NewOrders/NewOrdersSubheader/NewOrdersSubheader";
import ReviewOrderDetails from "components/NewOrders/Review/ReviewOrderDetails";
import ReviewOrderList from "components/NewOrders/Review/ReviewOrderList";
import NewOrdersHeader from "components/NewOrders/NewOrdersHeader/NewOrdersHeader";
import { type FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import ContentLoadingSkeleton from "pages/OrderPages/components/LoadingSkeleton/ContentLoadingSkeleton.tsx";
import { ReviewNewOrderErrorBannerStyles } from "components/Replacements/ErrorBannerStyles";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { PageProps } from "../BuildOrderPage/BuildOrderPage";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { CancelButtonText, NewOrderErrorMaintenanceHeader, NewOrderErrorMaintenanceSubHeader } from "constants/text";
import { getNewOrderURL } from "utils/string.ts";
import { DraftType, OrderType, UserRole } from "types/declarations/enums.ts";
import { useSubmitOrder } from "hooks/useSubmitOrder.ts";
import { useDialog } from "hooks/useDialog.ts";
import { uploadFileToBlob } from "utils/blob.ts";
import { cartActions } from "features/reducers/replacementOrder/cart.ts";
import useBlobStorage from "hooks/useBlobStorage.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { models } from "types/api/viewModels.ts";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import { useSubmitDraftOrderMutation } from "features/api/orderApi.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import SaveDraftModal from "components/NewOrders/SaveDraft/SaveDraftModal.tsx";
import useExportDraftQuote from "hooks/useExportDraftQuote.ts";

const ReviewOrderPage: FC<PageProps> = ({ isAddOn, isCustomColor }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const params = useParams();
	const { dialogAPIInfo } = useDialog();
	const { uploadContainerBlob } = useBlobStorage();
	const replacementCartItem = useSelector((state: RootState) =>
		state.cart.replacements.find((item) => item.id === "lowes-upload")
	);
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const { data: userDetails } = useGetUserDetailsQuery();
	const isInternalDesigner = useMemo(() => userDetails?.role === UserRole.INTERNAL_DESIGNER, [userDetails?.role]);
	const { exportFile } = useExportDraftQuote();

	const {
		handleSubmitOrder,
		showSkeleton,
		error: submitError,
		isLoading
	} = useSubmitOrder(Boolean(isAddOn), Boolean(isCustomColor), Boolean(isInternalDesigner));

	const handleBack = () => {
		dispatch(newOrderActions.clearDraftError());
		navigate(
			getNewOrderURL(
				isAddOn,
				isCustomColor,
				isInternalDesigner,
				isInternalDesigner ? "view-price-estimates" : "shipping-details",
				params.orderId
			)
		);
	};
	const [saveDraftErrorMessage, setSaveDraftErrorMessage] = useState<string | undefined>(undefined);
	const [isManuallySavingDraft, setIsManuallySavingDraft] = useState(false);
	const [submitDraftOrder, { isLoading: isSubmittingDraft }] = useSubmitDraftOrderMutation();
	const { snackbarOpen: draftSnackbarOpen } = useSnackbarEffect(
		isSubmittingDraft && isManuallySavingDraft,
		saveDraftErrorMessage
	);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSaveDraft = async (draft: models["DraftOrderViewModel"]) => {
		setIsManuallySavingDraft(true);
		const draftQuote = {
			...draft,
			type: DraftType.Quote,
			tandemOrderId: draft.draftOrderId
		} as models["DraftOrderViewModel"];
		try {
			setSaveDraftErrorMessage(undefined);
			await submitDraftOrder(draftQuote).unwrap();
			dispatch(newOrderActions.clearDraftError());
			exportFile(draftQuote);
			setTimeout(() => {
				setIsManuallySavingDraft(false);
				navigate(getNewOrderURL(isAddOn, isCustomColor, isInternalDesigner, "confirmation", params.orderId));
			}, 2000);
		} catch (error: any) {
			console.error("Save draft failed:", error);
			setSaveDraftErrorMessage("Failed to save draft. Please try again.");
			dispatch(newOrderActions.updateDraftError(error));
			setIsManuallySavingDraft(false);
		}
	};

	const handleContinue = async () => {
		let updatedOrderDetails = newOrderDetails;
		if (replacementCartItem && newOrderDetails) {
			const successfulUploads = await uploadToBlobStorage(newOrderDetails?.tandemOrderId ?? "");
			if (successfulUploads && newOrderDetails?.configurations?.[0]?.lineItems) {
				const updatedConfigurations = newOrderDetails.configurations.map((config, configIndex) => {
					if (configIndex === 0) {
						return {
							...config,
							lineItems:
								config.lineItems?.map((lineItem) => ({
									...lineItem,
									hasAttachments: lineItem.lineItemNumber === 1
								})) ?? []
						};
					}
					return config;
				});

				updatedOrderDetails = {
					...newOrderDetails,
					configurations: updatedConfigurations
				};

				dispatch(newOrderActions.overwriteOrderDetails(updatedOrderDetails));
			}
		}
		if (isInternalDesigner) {
			setIsModalOpen(true);
		} else {
			await handleSubmitOrder(false, updatedOrderDetails);
		}
	};

	if (isLoading || showSkeleton || dialogAPIInfo?.isLoadingAPI) {
		return <ContentLoadingSkeleton dataTestId="new-order-review-loading-skeleton" />;
	}

	const error = submitError ?? dialogAPIInfo?.apiError;

	const isMaintenanceError =
		error &&
		"errors" in error &&
		Array.isArray(error.errors) &&
		error.errors.length > 0 &&
		error.errors[0].code === 40001;

	const uploadToBlobStorage = async (uuID: string): Promise<string[]> => {
		const uploadedBlobs: string[] = [];
		const { cabinetFileUploads } = replacementCartItem ?? {};
		const combinedArray = [...(cabinetFileUploads ?? [])];

		for (const file of combinedArray) {
			try {
				const { fileUrl, size, name, id } = file;
				const fileName = `1/${name}`;
				await uploadFileToBlob({
					fileUrl,
					fileName,
					size,
					requestId: uuID,
					uploadContainerBlob
				});
				uploadedBlobs.push(id);
			} catch (e) {
				dispatch(cartActions.setErrorState());
				console.error(e);
			}
		}
		return uploadedBlobs;
	};

	return (
		<div>
			<NewOrdersHeader
				draftOrder={draftOrder}
				isReview
				isPDFExport
				isAddOn={isAddOn}
			/>
			<NewOrdersContentContainer>
				<NewOrdersContent>
					<NewOrdersSubheader
						title={
							draftOrder?.orderType === OrderType.QUOTE_ORDER
								? ProQuoteReviewPageTitle
								: NewOrderReviewPageTitle
						}
						dataTestId="new-order-review-order-label"
						handleSearch={() => {}}
						draftOrder={draftOrder}
						isAddOn={isAddOn}
					/>
					{error && (
						<ReviewNewOrderErrorBannerStyles data-testid="api-error-banner-header">
							<div>
								<div>
									<ErrorOutline />
								</div>
								<div>
									<h6>
										{isMaintenanceError ? NewOrderErrorMaintenanceHeader : NewOrderProcessError}
									</h6>
									<span>
										{isMaintenanceError ? NewOrderErrorMaintenanceSubHeader : NewOrderNotCorrect}
									</span>
									{"errors" in error && Array.isArray(error.errors) && !isMaintenanceError && (
										<ul>
											<li>
												{error.errors[0]?.message
													? error.errors[0].message
													: NewOrderGenericError}
											</li>
										</ul>
									)}
									{!isMaintenanceError && <span>{ContactCare}</span>}
									{"status" in error && error.status && (
										<span>({isMaintenanceError ? "40001" : `${error.status} Error`})</span>
									)}
								</div>
							</div>
						</ReviewNewOrderErrorBannerStyles>
					)}
					<ReviewOrderDetails
						isAddOn={isAddOn}
						isNewQuoteOrder={isInternalDesigner}
					/>
					<ReviewOrderList
						isNewQuoteOrder={isInternalDesigner}
						internalQuoteDetails={draftOrder}
					/>
					<FlowButtons>
						<Button
							data-testid="new-order-review-back-button"
							variant="text"
							onClick={handleBack}
						>
							{NewOrderBack}
						</Button>
						<Button
							data-testid="new-order-review-submit-order-button"
							data-id={`${isAddOn ? "add-ons" : "new-order"}-submit-button`}
							onClick={handleContinue}
							variant="contained"
						>
							{isInternalDesigner ? SubmitQuoteText : NewOrderSubmitOrder}
						</Button>
					</FlowButtons>
				</NewOrdersContent>
			</NewOrdersContentContainer>
			<Snackbar
				open={draftSnackbarOpen}
				isLoading={isSubmittingDraft}
				loadingText={SaveDraftHangTightText}
				successText={SaveDraftSuccessfullySavedText}
				isError={saveDraftErrorMessage !== undefined}
				errorText={saveDraftErrorMessage ?? ""}
			/>
			<SaveDraftModal
				draftOrder={draftOrder}
				closeModal={handleClose}
				saveDraftModalIsOpen={isModalOpen}
				firstButtonText={SaveAndDownloadNewOrderQuote}
				secondButtonText={CancelButtonText}
				draftType={DraftType.Quote}
				onSubmit={handleSaveDraft}
			/>
		</div>
	);
};

export default ReviewOrderPage;
